// src/features/previsionf/PrevisionFamilleView.tsx  — version v1
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  getForecastDepots,
  getForecastFamilles,
  getForecastHistorique,
  getForecastSousFamilles,
  tryPrevisionHorizon,
} from "../api";
import { session } from "../../../shared/api/session";
import { Spinner } from "../../../shared/ui/Spinner";
import { colors } from "../../../shared/ui/tokens";

// ── Types locaux ──────────────────────────────────────────────────────────────
interface FamilleDto {
  codFamp: string;
  designation: string;
}
interface DepotRecord {
  code_dep: string;
  ca_ventes: number;
  ca_retours: number;
  ca_net: number;
  nb_lignes: number;
  taux_retour: number;
}

interface ExtPrevisionFResponse {
  success: boolean;
  famille?: string;
  sous_famille?: string;
  depot?: string;
  horizon?: number;
  ca_total?: number | null;
  date_from?: string;
  date_to?: string;
  previsions?: Array<{
    step: number;
    date: string;
    ca_predit: number;
    borne_basse: number;
    borne_haute: number;
  }>;
  forecastCa: number | null;
  sameMonthHistory: Array<{ year: number; month: number; caTotal: number }>;
  weeklyHistory: Array<{ week: string; caTotal: number; nbJours: number }>;
  [key: string]: any;
}

type RowStatus = "idle" | "loading" | "loaded" | "error" | "nodata";

interface RowState {
  status: RowStatus;
  data?: ExtPrevisionFResponse;
  error?: string;
  forecastYear?: number;
  forecastMonth?: number;
  depot?: string | null;
  dateFrom?: string;
  dateTo?: string;
}

// ── Palette ───────────────────────────────────────────────────────────────────
const NW = {
  bg: colors.neutral[100],
  card: colors.neutral[0],
  violet: colors.brand[600],
  violetDark: colors.brand[700],
  violetMed: colors.accent[300],
  violetLight: colors.brand[100],
  violetBorder: "rgba(29,91,168,0.16)",
  text: colors.neutral[900],
  textMuted: colors.brand[500],
  green: colors.accent[500],
  greenBg: colors.neutral[50],
  red: colors.danger[500],
  redBg: colors.neutral[100],
  blue: colors.brand[500],
  blueBg: colors.brand[50],
  orange: colors.warning[600],
  orangeBg: colors.neutral[50],
  border: "rgba(29,91,168,0.13)",
  shadow: "0 2px 20px rgba(29,91,168,0.09)",
  shadowStrong: "0 4px 28px rgba(29,91,168,0.15)",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtCa = (v: number | undefined | null): string => {
  if (v == null || isNaN(v as number)) return "—";
  return (
    new Intl.NumberFormat("fr-TN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(v) + " DT"
  );
};

const fmtPct = (v: number) =>
  (v > 0 ? "▲ +" : v < 0 ? "▼ " : "") + v.toFixed(1) + "%";

function toYMD(d: Date): string {
  return d.toISOString().slice(0, 10);
}

async function fetchDepots(): Promise<DepotRecord[]> {
  try {
    const r = await getForecastDepots<any>();
    return (r.data ?? []).map((d: any) => ({
      ...d,
      code_dep: d.code_dep ?? d.codDep ?? d.cod_dep ?? "",
      ca_ventes: 0,
      ca_retours: 0,
      ca_net: 0,
      nb_lignes: 0,
      taux_retour: 0,
    }));
  } catch {
    return [];
  }
}

// ── DataCell ──────────────────────────────────────────────────────────────────
const DataCell: React.FC<{
  value: number | undefined | null;
  highlight?: boolean;
  muted?: boolean;
  compareWith?: number | null;
  incomplete?: boolean;
}> = ({ value, highlight, muted, compareWith, incomplete }) => {
  const hasCompare = compareWith != null && compareWith > 0 && value != null;
  const pct = hasCompare ? ((value! - compareWith!) / compareWith!) * 100 : null;
  const pctColor =
    pct == null ? NW.textMuted : pct > 0 ? NW.green : pct < 0 ? NW.red : NW.textMuted;

  return (
    <td
      style={{
        padding: "0 14px",
        textAlign: "right",
        verticalAlign: "middle",
        background: highlight ? "rgba(29,91,168,0.045)" : "transparent",
        borderLeft: highlight ? `3px solid ${NW.violet}22` : "none",
      }}
    >
      <div
        style={{
          fontWeight: highlight ? 800 : muted ? 600 : 700,
          fontSize: highlight ? 14 : 13,
          color: highlight
            ? NW.violet
            : muted
              ? NW.textMuted
              : value == null
                ? NW.border
                : NW.text,
          lineHeight: 1.2,
        }}
      >
        {fmtCa(value)}
      </div>
      {pct != null && (
        <div style={{ fontSize: 10, fontWeight: 700, color: pctColor, marginTop: 2 }}>
          {fmtPct(pct)}
        </div>
      )}
      {incomplete && (
        <div style={{ fontSize: 9, color: NW.orange, fontWeight: 700, marginTop: 2 }}>
          ⚠ sem. en cours
        </div>
      )}
    </td>
  );
};

// ── buildHistoryFields ────────────────────────────────────────────────────────
function buildHistoryFields(historique: any[]): {
  sameMonthHistory: any[];
  weeklyHistory: any[];
} {
  if (!historique || historique.length === 0) {
    return { sameMonthHistory: [], weeklyHistory: [] };
  }

  const sorted = [...historique].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
  const weeklyHistory: any[] = [];
  if (sorted.length >= 1)
    weeklyHistory.push({ week: "S-1", caTotal: sorted[0].ca_reel, nbJours: 7 });
  if (sorted.length >= 2)
    weeklyHistory.push({ week: "S-2", caTotal: sorted[1].ca_reel, nbJours: 7 });

  const byMonth: Record<string, { year: number; month: number; caTotal: number }> = {};
  for (const row of historique) {
    const d = new Date(row.date);
    const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
    if (!byMonth[key]) {
      byMonth[key] = { year: d.getFullYear(), month: d.getMonth() + 1, caTotal: 0 };
    }
    byMonth[key].caTotal += row.ca_reel ?? 0;
  }
  const sameMonthHistory = Object.values(byMonth).sort(
    (a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month),
  );

  return { sameMonthHistory, weeklyHistory };
}

function mapApiResponse(res: any, historique: any[] = []): ExtPrevisionFResponse {
  const caTotal: number | null = res.ca_total ?? null;
  const { sameMonthHistory, weeklyHistory } = buildHistoryFields(historique);
  return { ...res, forecastCa: caTotal, sameMonthHistory, weeklyHistory } as ExtPrevisionFResponse;
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface PrevisionFamilleViewProps {
  onSwitchToSousFamille?: () => void;
  onSwitchToHorizon?: () => void;
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================
const PrevisionFamilleView: React.FC<PrevisionFamilleViewProps> = ({
  onSwitchToSousFamille = () => {},
  onSwitchToHorizon = () => {},
}) => {
  const [depots, setDepots] = useState<DepotRecord[]>([]);
  const [familles, setFamilles] = useState<FamilleDto[]>([]);
  const [loadingF, setLoadingF] = useState(false);

  // ── Rôle utilisateur ──────────────────────────────────────────────────────
  const isAdmin = (() => {
    try {
      const u = session.getUser<Record<string, any>>() ?? {};
      const role: string = (u?.nom ?? u?.role ?? u?.idRole ?? "").toString().toLowerCase();
      return role.includes("admin") || role === "1";
    } catch { return false; }
  })();

  const userDepot = (() => {
    try {
      const u = session.getUser<Record<string, any>>() ?? {};
      return u?.depot ?? u?.Depot ?? u?.codDep ?? "";
    } catch { return ""; }
  })();

  const [selectedDepot, setSelectedDepot] = useState<string>("");

  // Sélectionner automatiquement le dépôt de l'utilisateur (non-admin) ou le 1er (admin)
  useEffect(() => {
    if (depots.length === 0) return;
    if (!isAdmin && userDepot) {
      setSelectedDepot(userDepot);
    } else if (selectedDepot === "") {
      const firstCode = depots[0].code_dep ?? (depots[0] as any).codDep ?? "";
      setSelectedDepot(firstCode);
    }
  }, [depots]);

  // Compatibilité avec la logique existante (activeDepot)
  const selectedDepots = new Set(selectedDepot ? [selectedDepot] : []);
  const [searchText, setSearchText] = useState("");
  const [horizon, setHorizon] = useState<number>(4);
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const loadingAll = useRef(false);


  // ── Charger toutes les familles au montage ────────────────────────────────
  useEffect(() => {
    setLoadingF(true);
    getForecastFamilles<FamilleDto>()
      .then((r) => setFamilles(r.data ?? []))
      .catch(() => setFamilles([]))
      .finally(() => setLoadingF(false));
  }, []);

  useEffect(() => {
    fetchDepots().then((deps) => setDepots(deps));
  }, []);

  useEffect(() => {
    setRowStates({});
  }, [horizon, selectedDepot]);

  const periodValid = true;
  const activeDepot = selectedDepots.size === 1 ? [...selectedDepots][0] : null;

  const colS2Label = "Sem S−2";
  const colS1Label = "Sem S−1";
  const colM2Label = "Mois −2";
  const colM1Label = "Mois −1";
  const colForecastLabel = `Prévision ${horizon} semaine${horizon > 1 ? "s" : ""}`;

  const items = familles.filter((item) => {
    const label = item.designation ?? item.codFamp;
    const code = item.codFamp;
    const q = searchText.toLowerCase();
    return !q || label.toLowerCase().includes(q) || code.toLowerCase().includes(q);
  });

  const getCode = (item: FamilleDto) => item.codFamp;
  const getLabel = (item: FamilleDto) => item.designation ?? item.codFamp;

  // ── loadRow : charge prévision + historique pour une famille ─────────────
  const loadRow = useCallback(
    async (code: string, label: string) => {
      const current = rowStates[code];
      if (current?.status === "loading") return;
      if (current?.status === "loaded" && current.depot === activeDepot) {
        setRowStates((prev) => ({ ...prev, [code]: { status: "idle" } }));
        return;
      }
      if (current?.status === "nodata") return;

      if (!activeDepot) {
        setRowStates((prev) => ({
          ...prev,
          [code]: { status: "error", error: "Veuillez sélectionner un seul dépôt" },
        }));
        return;
      }

      // APRÈS (loadRow dans PrevisionFamilleView)
    setRowStates((prev) => ({ ...prev, [code]: { status: "loading" } }));
    try {
      // 1. Récupérer toutes les sous-familles de cette famille
      const sfRes = await getForecastSousFamilles(code).catch(() => ({ data: [] }));
      const sousFamilles: string[] = (sfRes.data ?? []).map((sf: any) => sf.codFam);

      if (sousFamilles.length === 0) {
        setRowStates((prev) => ({ ...prev, [code]: { status: "nodata" } }));
        return;
      }

      // 2. Lancer les prévisions en parallèle pour chaque sous-famille
      const results = await Promise.all(
        sousFamilles.map((sf) =>
          tryPrevisionHorizon({ famille: code, sous_famille: sf, horizon, depot: activeDepot })
        )
      );

      // 3. Sommer les CA prédits semaine par semaine
      const successResults = results.filter((r) => r.success && r.previsions?.length);
      if (successResults.length === 0) {
        setRowStates((prev) => ({ ...prev, [code]: { status: "nodata" } }));
        return;
      }

      // Agréger par step
      const stepsMap: Record<number, { ca_predit: number; borne_basse: number; borne_haute: number; date: string }> = {};
      for (const r of successResults) {
        for (const p of r.previsions ?? []) {
          if (!stepsMap[p.step]) {
            stepsMap[p.step] = { ca_predit: 0, borne_basse: 0, borne_haute: 0, date: p.date };
          }
          stepsMap[p.step].ca_predit  += p.ca_predit;
          stepsMap[p.step].borne_basse += p.borne_basse;
          stepsMap[p.step].borne_haute += p.borne_haute;
        }
      }

      const previsions = Object.entries(stepsMap)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([step, v]) => ({ step: Number(step), ...v }));

      const caTotal = previsions.reduce((s, p) => s + p.ca_predit, 0);
      const aggregated = {
        success: true,
        famille: code,
        depot: activeDepot,
        horizon,
        ca_total: caTotal,
        date_from: previsions[0]?.date,
        date_to:   previsions[previsions.length - 1]?.date,
        previsions,
      };

      // APRÈS
      const mapped = mapApiResponse(aggregated, []);
      if (!mapped.forecastCa) {
        setRowStates((prev) => ({ ...prev, [code]: { status: "nodata" } }));
      } else {
        setRowStates((prev) => ({
          ...prev,
          [code]: {
            status: "loaded",
            data: mapped,
            depot: activeDepot,
            dateFrom: aggregated.date_from,
            dateTo: aggregated.date_to,
          },
        }));
      }
    } catch (e: any) {
      setRowStates((prev) => ({
        ...prev,
        [code]: { status: "error", error: e.message ?? "Erreur réseau" },
      }));
    }
  },
  [activeDepot, rowStates, horizon],
);

  // ── loadAll : charge toutes les familles d'un coup ────────────────────────
  const loadAll = useCallback(async () => {
    if (loadingAll.current) return;
    loadingAll.current = true;

    const codesToLoad = items
      .map((item) => ({ code: getCode(item), label: getLabel(item) }))
      .filter(
        ({ code }) =>
          !rowStates[code] ||
          rowStates[code].status === "idle" ||
          rowStates[code].status === "error",
      );

    if (!activeDepot) {
      loadingAll.current = false;
      setRowStates((prev) => {
        const next = { ...prev };
        codesToLoad.forEach(({ code }) => {
          next[code] = { status: "error", error: "Sélectionnez un seul dépôt" };
        });
        return next;
      });
      return;
    }

    setRowStates((prev) => {
      const next = { ...prev };
      codesToLoad.forEach(({ code }) => {
        if (!next[code] || next[code].status !== "loaded")
          next[code] = { status: "loading" };
      });
      return next;
    });

    for (const { code } of codesToLoad) {
      try {
        // Prévision + historique réel en parallèle ; un échec de l'historique n'est pas bloquant
        const [res, histData] = await Promise.all([
          tryPrevisionHorizon({ famille: code, sous_famille: code, horizon, depot: activeDepot }),
          getForecastHistorique({ famille: code, sousFamille: code, depot: activeDepot!, n: 12 })
            .then((r) => (r?.data ?? []) as any[])
            .catch(() => [] as any[]),
        ]);

        if (res.success) {
          const mapped = mapApiResponse(res, histData);
          setRowStates((prev) => ({
            ...prev,
            [code]: mapped.forecastCa
              ? {
                  status: "loaded",
                  data: mapped,
                  depot: activeDepot,
                  dateFrom: res.date_from,
                  dateTo: res.date_to,
                }
              : { status: "nodata" },
          }));
        } else {
          const errMsg = res.error ?? "Erreur";
          const isNoData =
            errMsg.toLowerCase().includes("aucun") ||
            errMsg.toLowerCase().includes("no data") ||
            errMsg.toLowerCase().includes("introuvable");
          setRowStates((prev) => ({
            ...prev,
            [code]: { status: isNoData ? "nodata" : "error", error: errMsg },
          }));
        }
      } catch (e: any) {
        setRowStates((prev) => ({
          ...prev,
          [code]: { status: "error", error: e.message ?? "Erreur réseau" },
        }));
      }
    }
    loadingAll.current = false;
  }, [activeDepot, items, rowStates, horizon]);

  // ── Compteurs ─────────────────────────────────────────────────────────────
  const countLoaded = Object.values(rowStates).filter((r) => r.status === "loaded").length;
  const countLoading = Object.values(rowStates).filter((r) => r.status === "loading").length;
  const countError = Object.values(rowStates).filter((r) => r.status === "error").length;
  const countNoData = Object.values(rowStates).filter((r) => r.status === "nodata").length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: "calc(100vh - 62px)",
        background: NW.bg,
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
        padding: "22px 24px",
      }}
    >
      <style>{`
        @keyframes nw-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .nw-row:hover { background: ${NW.violetLight} !important; cursor: pointer; }
        .nw-row-loaded { background: rgba(29,91,168,0.025) !important; }
        .nw-depot-label:hover { border-color: ${NW.violetMed} !important; }
        .nw-load-btn:hover { background: ${NW.violetDark} !important; }
      `}</style>

      {/* ── En-tête ──────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            background: "linear-gradient(135deg,#184B8C,#1D5BA8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            boxShadow: "0 4px 16px rgba(29,91,168,0.35)",
            flexShrink: 0,
          }}
        >
          📦
        </div>
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h1
            style={{
              margin: 0,
              fontFamily: "Poppins, Inter, system-ui, sans-serif",
              fontWeight: 800,
              fontSize: 22,
              color: NW.text,
              letterSpacing: "-0.4px",
            }}
          >
            Prévision par Famille
          </h1>

          {/* ── Boutons navigation ─────────────────────────────────────────── */}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              style={{
                padding: "8px 16px",
                borderRadius: 10,
                border: "none",
                background: NW.violet,
                color: "#FFFFFF",
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              📦 Famille
            </button>
            <button
              onClick={onSwitchToSousFamille}
              style={{
                padding: "8px 16px",
                borderRadius: 10,
                border: `1.5px solid ${NW.violetBorder}`,
                background: "#FFFFFF",
                color: NW.violet,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              🗂 Sous-famille
            </button>
            <button
                onClick={onSwitchToHorizon}

              style={{
                padding: "8px 16px",
                borderRadius: 10,
                border: `1.5px solid ${NW.violetBorder}`,
                background: "#FFFFFF",
                color: NW.violet,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
               🔍 Filtre

            </button>
          </div>
        </div>

        {/* Compteurs */}
        {countLoaded + countLoading + countError + countNoData > 0 && (
          <div style={{ display: "flex", gap: 8, fontSize: 11, fontWeight: 700 }}>
            {countLoaded > 0 && (
              <span
                style={{
                  background: NW.greenBg,
                  color: NW.green,
                  padding: "4px 10px",
                  borderRadius: 99,
                }}
              >
                ✓ {countLoaded} chargée{countLoaded > 1 ? "s" : ""}
              </span>
            )}
            {countLoading > 0 && (
              <span
                style={{
                  background: NW.violetLight,
                  color: NW.violet,
                  padding: "4px 10px",
                  borderRadius: 99,
                }}
              >
                ⏳ {countLoading}
              </span>
            )}
            {countNoData > 0 && (
              <span
                style={{
                  background: NW.redBg,
                  color: NW.red,
                  padding: "4px 10px",
                  borderRadius: 99,
                }}
              >
                ∅ {countNoData} sans données
              </span>
            )}
            {countError > 0 && (
              <span
                style={{
                  background: NW.orangeBg,
                  color: NW.orange,
                  padding: "4px 10px",
                  borderRadius: 99,
                }}
              >
                ⚠ {countError} erreur{countError > 1 ? "s" : ""}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Filtres : dépôt + horizon ─────────────────────────────────────────── */}
      <div
        style={{
          background: NW.card,
          borderRadius: 16,
          border: `1.5px solid ${NW.violetBorder}`,
          boxShadow: NW.shadow,
          padding: "16px 20px",
          marginBottom: 16,
          display: "flex",
          gap: 24,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        {/* Dépôt */}
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              color: NW.textMuted,
              textTransform: "uppercase",
              letterSpacing: 1.2,
              marginBottom: 8,
            }}
          >
            🏪 Dépôt
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {(isAdmin ? depots : depots.filter(d => !userDepot || d.code_dep === userDepot)).map((dep, idx) => {
              const depCode = dep.code_dep || String(idx);
              const isChecked = selectedDepot === depCode;
              return (
                <label
                  key={`depot-${depCode}-${idx}`}
                  className="nw-depot-label"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    cursor: "pointer",
                    padding: "5px 11px",
                    borderRadius: 8,
                    border: `1.5px solid ${isChecked ? NW.violet : NW.border}`,
                    background: isChecked ? NW.violetLight : "transparent",
                    fontSize: 12,
                    fontWeight: 600,
                    color: isChecked ? NW.violet : NW.text,
                    userSelect: "none",
                    transition: "all 0.15s",
                  }}
                >
                  <input
                    type="radio"
                    name="depot-famille"
                    checked={isChecked}
                    onChange={() => setSelectedDepot(depCode)}
                    style={{ accentColor: NW.violet, width: 13, height: 13 }}
                  />
                  {depCode}
                </label>
              );
            })}
          </div>
        </div>

        <div
          style={{
            width: 1,
            background: NW.border,
            alignSelf: "stretch",
            flexShrink: 0,
          }}
        />

        {/* Horizon */}
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              color: NW.textMuted,
              textTransform: "uppercase",
              letterSpacing: 1.2,
              marginBottom: 8,
            }}
          >
            📅 Horizon (semaines)
          </div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((h) => (
              <button
                key={h}
                onClick={() => setHorizon(h)}
                style={{
                  width: 44,
                  height: 36,
                  borderRadius: 10,
                  border: `1.5px solid ${horizon === h ? NW.violet : NW.violetBorder}`,
                  background: horizon === h ? NW.violet : "transparent",
                  color: horizon === h ? "#FFFFFF" : NW.text,
                  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {h}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 10, color: NW.textMuted, fontWeight: 600, marginTop: 6 }}>
            Horizon sélectionné :{" "}
            <strong style={{ color: NW.violet }}>
              {horizon} semaine{horizon > 1 ? "s" : ""}
            </strong>
          </div>
        </div>
      </div>

      {/* ── Barre outils tableau ──────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        <input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Filtrer…"
          style={{
            padding: "7px 13px",
            fontSize: 12,
            border: `1.5px solid ${NW.border}`,
            borderRadius: 10,
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            color: NW.text,
            background: NW.card,
            outline: "none",
            width: 160,
          }}
        />
        <span style={{ fontSize: 12, color: NW.textMuted, fontWeight: 600 }}>
          {loadingF ? <Spinner /> : `${items.length} famille(s)`}
        </span>
        {periodValid && (
          <button
            className="nw-load-btn"
            onClick={loadAll}
            disabled={countLoading > 0}
            style={{
              marginLeft: "auto",
              padding: "7px 16px",
              fontSize: 12,
              fontWeight: 700,
              border: "none",
              borderRadius: 10,
              background:
                countLoading > 0
                  ? "#B0CBEB"
                  : "linear-gradient(135deg,#184B8C,#1D5BA8)",
              color: "#FFFFFF",
              cursor: countLoading > 0 ? "not-allowed" : "pointer",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              display: "flex",
              alignItems: "center",
              gap: 6,
              boxShadow:
                countLoading > 0 ? "none" : "0 2px 12px rgba(29,91,168,0.3)",
              transition: "all 0.15s",
            }}
          >
            {countLoading > 0 ? (
              <>
                <Spinner /> Chargement…
              </>
            ) : (
              "⚡ Charger tout"
            )}
          </button>
        )}
        {countLoaded + countError + countNoData > 0 && (
          <button
            onClick={() => setRowStates({})}
            style={{
              padding: "7px 14px",
              fontSize: 12,
              fontWeight: 600,
              border: `1.5px solid ${NW.border}`,
              borderRadius: 10,
              background: "transparent",
              color: NW.textMuted,
              cursor: "pointer",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
          >
            🔄 Réinitialiser
          </button>
        )}
      </div>

      {/* ── Tableau principal ─────────────────────────────────────────────────── */}
      <div
        style={{
          background: NW.card,
          borderRadius: 18,
          border: `1.5px solid ${NW.violetBorder}`,
          boxShadow: NW.shadowStrong,
          overflow: "hidden",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ background: NW.violetLight }}>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    fontWeight: 800,
                    fontSize: 11,
                    color: NW.violet,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    borderBottom: `2px solid ${NW.violetBorder}`,
                    minWidth: 200,
                    position: "sticky",
                    left: 0,
                    background: NW.violetLight,
                    zIndex: 2,
                  }}
                >
                  📦 Famille
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "right",
                    fontWeight: 800,
                    fontSize: 11,
                    color: NW.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    borderBottom: `2px solid ${NW.violetBorder}`,
                    minWidth: 130,
                    whiteSpace: "nowrap",
                  }}
                >
                  {colM2Label}
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "right",
                    fontWeight: 800,
                    fontSize: 11,
                    color: NW.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    borderBottom: `2px solid ${NW.violetBorder}`,
                    minWidth: 130,
                    whiteSpace: "nowrap",
                  }}
                >
                  {colM1Label}
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "right",
                    fontWeight: 800,
                    fontSize: 11,
                    color: NW.blue,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    borderBottom: `2px solid ${NW.violetBorder}`,
                    minWidth: 150,
                    whiteSpace: "nowrap",
                  }}
                >
                  {colS2Label}
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "right",
                    fontWeight: 800,
                    fontSize: 11,
                    color: NW.blue,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    borderBottom: `2px solid ${NW.violetBorder}`,
                    minWidth: 150,
                    whiteSpace: "nowrap",
                  }}
                >
                  {colS1Label}
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "right",
                    fontWeight: 800,
                    fontSize: 11,
                    color: NW.violet,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    borderBottom: `2px solid ${NW.violetBorder}`,
                    minWidth: 180,
                    whiteSpace: "nowrap",
                    background: "rgba(29,91,168,0.06)",
                    borderLeft: `3px solid ${NW.violet}22`,
                  }}
                >
                  🔮 {colForecastLabel}
                </th>
              </tr>
              <tr style={{ background: "rgba(29,91,168,0.015)" }}>
                <td
                  style={{
                    padding: "4px 16px",
                    fontSize: 10,
                    color: NW.textMuted,
                    borderBottom: `1px solid ${NW.border}`,
                    position: "sticky",
                    left: 0,
                    background: "rgba(238,244,251,0.3)",
                  }}
                >
                  Cliquer sur une ligne pour charger
                </td>
                {["CA mois", "CA mois", "Sem. réelle", "Sem. réelle", "Prévision"].map(
                  (s, i) => (
                    <td
                      key={i}
                      style={{
                        padding: "4px 16px",
                        fontSize: 10,
                        color: i === 4 ? NW.violet : NW.textMuted,
                        textAlign: "right",
                        borderBottom: `1px solid ${NW.border}`,
                        fontStyle: "italic",
                        background: i === 4 ? "rgba(29,91,168,0.02)" : "transparent",
                        fontWeight: i === 4 ? 700 : 400,
                      }}
                    >
                      {s}
                    </td>
                  ),
                )}
              </tr>
            </thead>

            <tbody>
              {loadingF ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{ padding: 48, textAlign: "center", color: NW.textMuted }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <Spinner />
                      <span style={{ fontSize: 13, fontWeight: 600 }}>
                        Chargement des familles…
                      </span>
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      padding: 48,
                      textAlign: "center",
                      color: NW.textMuted,
                      fontSize: 13,
                    }}
                  >
                    {searchText
                      ? `Aucun résultat pour « ${searchText} »`
                      : "Aucune famille disponible"}
                  </td>
                </tr>
              ) : (
                items.map((item, idx) => {
                  const code = getCode(item);
                  const label = getLabel(item);
                  const rs = rowStates[code];
                  const status: RowStatus = rs?.status ?? "idle";
                  const isLoaded = status === "loaded";
                  const isLoading = status === "loading";
                  const isError = status === "error";
                  const isNoData = status === "nodata";
                  const isActive = isLoaded || isLoading || isError || isNoData;

                  const hist = rs?.data?.sameMonthHistory ?? [];
                  const weekly = rs?.data?.weeklyHistory ?? [];
                  const fy = rs?.forecastYear;

                  const today = new Date().toISOString().slice(0, 10);

                  // Mois −1 et −2 : chercher dans sameMonthHistory
                  const now = new Date();
                  const m1Month = now.getMonth() === 0 ? 12 : now.getMonth();
                  const m1Year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
                  const m2Month = m1Month === 1 ? 12 : m1Month - 1;
                  const m2Year = m1Month === 1 ? m1Year - 1 : m1Year;

                  const m1data = (hist as any[]).find(
                    (h) => h.year === m1Year && h.month === m1Month,
                  );
                  const m2data = (hist as any[]).find(
                    (h) => h.year === m2Year && h.month === m2Month,
                  );

                  const m1ca = m1data?.caTotal ?? null;
                  const m2ca = m2data?.caTotal ?? null;

                  const s2 = (weekly as any[]).find((w) => w.week === "S-2") ?? null;
                  const s1 = (weekly as any[]).find((w) => w.week === "S-1") ?? null;
                  const forecast = rs?.data?.forecastCa ?? null;

                  const yoyPct =
                    forecast != null && m1ca != null && m1ca > 0
                      ? ((forecast - m1ca) / m1ca) * 100
                      : null;

                  return (
                    <tr
                      key={`${code}-${idx}`}
                      className={`nw-row${isLoaded ? " nw-row-loaded" : ""}`}
                      onClick={() => loadRow(code, label)}
                      style={{
                        borderBottom: `1px solid ${NW.border}`,
                        transition: "background 0.12s",
                        height: 54,
                      }}
                    >
                      {/* Colonne famille */}
                      <td
                        style={{
                          padding: "0 16px",
                          position: "sticky",
                          left: 0,
                          background: isLoaded ? "rgba(29,91,168,0.025)" : NW.card,
                          zIndex: 1,
                          borderRight: `1px solid ${NW.border}`,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 5,
                              border: `2px solid ${isActive ? NW.violet : NW.border}`,
                              background: isActive
                                ? isNoData
                                  ? NW.red
                                  : NW.violet
                                : "transparent",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              transition: "all 0.12s",
                            }}
                          >
                            {isLoading ? (
                              <Spinner />
                            ) : isLoaded ? (
                              <span
                                style={{
                                  color: "#FFFFFF",
                                  fontSize: 9,
                                  lineHeight: 1,
                                  fontWeight: 900,
                                }}
                              >
                                ✓
                              </span>
                            ) : isError ? (
                              <span style={{ color: "#FFFFFF", fontSize: 9, lineHeight: 1 }}>
                                ✕
                              </span>
                            ) : isNoData ? (
                              <span style={{ color: "#FFFFFF", fontSize: 9, lineHeight: 1 }}>
                                ∅
                              </span>
                            ) : null}
                          </div>
                          <div>
                            <div
                              style={{
                                fontWeight: isActive ? 800 : 600,
                                color: isNoData
                                  ? NW.red
                                  : isActive
                                    ? NW.violet
                                    : NW.text,
                                fontSize: 13,
                                lineHeight: 1.2,
                              }}
                            >
                              {label}
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: NW.textMuted,
                                fontWeight: 600,
                              }}
                            >
                              {code}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Colonnes données */}
                      {isLoading ? (
                        <td
                          colSpan={5}
                          style={{
                            padding: "0 16px",
                            textAlign: "center",
                            color: NW.textMuted,
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <Spinner /> Calcul de la prévision…
                          </div>
                        </td>
                      ) : isNoData ? (
                        <td
                          colSpan={5}
                          style={{
                            padding: "0 16px",
                            color: NW.red,
                            fontSize: 12,
                            fontWeight: 700,
                            textAlign: "center",
                          }}
                        >
                          ⚠ Aucune donnée trouvée
                        </td>
                      ) : isError ? (
                        <td
                          colSpan={5}
                          style={{
                            padding: "0 16px",
                            color: NW.red,
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          ❌ {rs.error}
                        </td>
                      ) : isLoaded ? (
                        <React.Fragment key={`${code}-loaded`}>
                          <DataCell value={m2ca} />
                          <DataCell value={m1ca} compareWith={m2ca} />
                          <DataCell
                            value={s2?.caTotal ?? null}
                            muted
                            incomplete={s2 != null && s2.nbJours < 7}
                          />
                          <DataCell
                            value={s1?.caTotal ?? null}
                            muted
                            compareWith={s2?.caTotal ?? null}
                          />
                          <td
                            style={{
                              padding: "0 16px",
                              textAlign: "right",
                              verticalAlign: "middle",
                              background: "rgba(29,91,168,0.06)",
                              borderLeft: `3px solid ${NW.violet}22`,
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 900,
                                fontSize: 14,
                                color: NW.violet,
                                lineHeight: 1.2,
                              }}
                            >
                              {fmtCa(forecast)}
                            </div>
                            {yoyPct != null && (
                              <div
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color:
                                    yoyPct > 0
                                      ? NW.green
                                      : yoyPct < 0
                                        ? NW.red
                                        : NW.textMuted,
                                  marginTop: 2,
                                }}
                              >
                                {fmtPct(yoyPct)} vs M−1
                              </div>
                            )}
                          </td>
                        </React.Fragment>
                      ) : (
                        <React.Fragment key={`${code}-idle`}>
                          {[0, 1, 2, 3].map((i) => (
                            <td
                              key={i}
                              style={{
                                padding: "0 16px",
                                textAlign: "right",
                                color: NW.border,
                                fontSize: 12,
                              }}
                            >
                              {periodValid ? "—" : ""}
                            </td>
                          ))}
                          <td
                            style={{
                              padding: "0 16px",
                              textAlign: "right",
                              background: "rgba(29,91,168,0.03)",
                              borderLeft: `3px solid ${NW.violet}11`,
                            }}
                          >
                            {periodValid ? (
                              <span
                                style={{
                                  fontSize: 11,
                                  color: NW.violetMed,
                                  fontWeight: 600,
                                  fontStyle: "italic",
                                }}
                              >
                                ← Cliquer pour charger
                              </span>
                            ) : (
                              <span style={{ fontSize: 11, color: NW.border }}>
                                Choisir une période ↑
                              </span>
                            )}
                          </td>
                        </React.Fragment>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>

            {/* ── Ligne TOTAL ───────────────────────────────────────────────── */}
            {countLoaded > 1 &&
              (() => {
                const loadedRows = Object.values(rowStates).filter(
                  (r) => r.status === "loaded" && r.data,
                );
                const now = new Date();
                const m1Month = now.getMonth() === 0 ? 12 : now.getMonth();
                const m1Year =
                  now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
                const m2Month = m1Month === 1 ? 12 : m1Month - 1;
                const m2Year = m1Month === 1 ? m1Year - 1 : m1Year;

                const totalForecast = loadedRows.reduce(
                  (s, r) => s + (r.data?.forecastCa ?? 0),
                  0,
                );
                const totalM1 = loadedRows.reduce((s, r) => {
                  const entry = (r.data?.sameMonthHistory as any[])?.find(
                    (h) => h.year === m1Year && h.month === m1Month,
                  );
                  return s + (entry?.caTotal ?? 0);
                }, 0);
                const totalM2 = loadedRows.reduce((s, r) => {
                  const entry = (r.data?.sameMonthHistory as any[])?.find(
                    (h) => h.year === m2Year && h.month === m2Month,
                  );
                  return s + (entry?.caTotal ?? 0);
                }, 0);
                const totalS1 = loadedRows.reduce(
                  (s, r) =>
                    s +
                    ((r.data?.weeklyHistory as any[])?.find((w) => w.week === "S-1")
                      ?.caTotal ?? 0),
                  0,
                );
                const totalS2 = loadedRows.reduce(
                  (s, r) =>
                    s +
                    ((r.data?.weeklyHistory as any[])?.find((w) => w.week === "S-2")
                      ?.caTotal ?? 0),
                  0,
                );
                return (
                  <tfoot>
                    <tr
                      style={{
                        background: NW.violetLight,
                        borderTop: `2px solid ${NW.violetBorder}`,
                      }}
                    >
                      <td
                        style={{
                          padding: "10px 16px",
                          fontWeight: 800,
                          fontSize: 12,
                          color: NW.violet,
                          position: "sticky",
                          left: 0,
                          background: NW.violetLight,
                        }}
                      >
                        TOTAL ({countLoaded} familles chargées)
                      </td>
                      <td
                        style={{
                          padding: "10px 16px",
                          textAlign: "right",
                          fontWeight: 800,
                          fontSize: 13,
                          color: NW.text,
                        }}
                      >
                        {fmtCa(totalM2)}
                      </td>
                      <td
                        style={{
                          padding: "10px 16px",
                          textAlign: "right",
                          fontWeight: 800,
                          fontSize: 13,
                          color: NW.text,
                        }}
                      >
                        {fmtCa(totalM1)}
                      </td>
                      <td
                        style={{
                          padding: "10px 16px",
                          textAlign: "right",
                          fontWeight: 700,
                          fontSize: 12,
                          color: NW.textMuted,
                        }}
                      >
                        {fmtCa(totalS2)}
                      </td>
                      <td
                        style={{
                          padding: "10px 16px",
                          textAlign: "right",
                          fontWeight: 700,
                          fontSize: 12,
                          color: NW.textMuted,
                        }}
                      >
                        {fmtCa(totalS1)}
                      </td>
                      <td
                        style={{
                          padding: "10px 16px",
                          textAlign: "right",
                          fontWeight: 900,
                          fontSize: 15,
                          color: NW.violet,
                          background: "rgba(29,91,168,0.08)",
                          borderLeft: `3px solid ${NW.violet}33`,
                        }}
                      >
                        {fmtCa(totalForecast)}
                      </td>
                    </tr>
                  </tfoot>
                );
              })()}
          </table>
        </div>
      </div>

      {/* ── Légende ───────────────────────────────────────────────────────────── */}
      <div
        style={{
          marginTop: 14,
          display: "flex",
          gap: 20,
          fontSize: 11,
          color: NW.textMuted,
          flexWrap: "wrap",
        }}
      >
        <span>
          📅 <strong>Sem. S-1</strong> = dernière semaine disponible dans l'historique
        </span>
        <span>
          📅 <strong>Sem. S-2</strong> = avant-dernière semaine
        </span>
        <span>
          📅 <strong>Mois −1 / −2</strong> = mois calendaires précédant le mois en cours
        </span>
        <span>🔵 Les % = variation vs la colonne précédente</span>
      </div>
    </div>
  );
};

export default PrevisionFamilleView;